import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import { List } from 'immutable';
import classnames from 'classnames';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';
import Button from 'react-bootstrap/lib/Button';

import { OrderSummaryContexts } from '../../constants/AppConstants';
import OrderActions from '../../actions/OrderActions';
import Order from '../../records/Order';
import OrderTrip from '../../records/order/Trip';
import { translate as t } from '../../lib/I18n';
import { deriveSplitDisplayValues, getGroupedTrips, groupFaresByPassenger } from '../../utils/splits';

import Icon from '../general/Icon';
import TripPlusbus from './TripPlusbus';
import Price from '../general/Price';
import RemoveJourneyModal from './RemoveJourneyModal';
import DownloadTicketsModal from './DownloadTicketsModal';
import FeatureFlag from '../general/FeatureFlag';
import AddFavouriteLink from '../fares/AddFavouriteLink';
import CreateAlert from '../disruptionAlert/CreateAlert';
import Fare from './Fare';
import AmendedSplitFares from '../account/amend/split/AmendedSplitFares';
import FareStations from './FareStations';
import OnEnterTrigger from '../general/OnEnterTrigger';
import TicketCalculationsSummary from "../account/refund/TicketCalculationsSummary";
import CarbonFootprintButton from '../carbonFootprint/CarbonFootprintButton';

class Trip extends React.PureComponent {

    static propTypes = {
        index: PropTypes.number.isRequired,
        order: PropTypes.instanceOf(Order).isRequired,
        orderTrip: PropTypes.instanceOf(OrderTrip).isRequired,
        open: PropTypes.bool,
        isLocked: PropTypes.bool,
        isLoggedIn: PropTypes.bool,
        isFareOnly: PropTypes.bool,
        showAmendedTrip: PropTypes.bool,
        refundDetails: PropTypes.instanceOf(Order),
        storedRefundCalculations: PropTypes.instanceOf(List),
        context: PropTypes.oneOf(_.values(OrderSummaryContexts)),
        matchingTimetableAlert: PropTypes.object,
        setBasketChanged: PropTypes.func,
        additionalOrderTrips: PropTypes.instanceOf(List),
        expandFaresPerTicket: PropTypes.bool,
    };

    static defaultProps = {
        open: false,
        isLocked: false,
        isLoggedIn: false,
        isFareOnly: false,
        showAmendedTrip: true,
        storedRefundCalculations: new List(),
        context: null,
        matchingTimetableAlert: null,
        setBasketChanged: _.noop,
        additionalOrderTrips: new List(),
        expandFaresPerTicket: false,
    };

    state = {
        open: this.props.order.hasRefunds() ? false : this.props.open,
        previousOpen: false,
        journeyToRemove: null,
        showDownloads: false
    };

    getFare() {
        const { isFareOnly, orderTrip } = this.props;
        return isFareOnly ? orderTrip.getFareOnlyFare() : orderTrip.getOutwardFare();
    }

    getGroupTrips() {
        const { orderTrip, additionalOrderTrips } = this.props;
        return List.of(orderTrip).concat(additionalOrderTrips);
    }

    getGroupTotalPrice() {
        return this.getGroupTrips().reduce((sum, trip) => sum + trip.getTotalPrice(), 0);
    }

    getTripWithPlusBus() {
        return this.getGroupTrips().find(trip => trip.hasPlusBus()) || this.props.orderTrip;
    }

    isSplitGroup() {
        return this.getGroupTrips().some(trip => trip.isSplitTrip?.());
    }

    getSplitReturnTrips() {
        const { returnSingleItems = [], returnItems = [] } = getGroupedTrips({
            items: this.getGroupTrips().toArray(),
        });

        return returnItems.length ? returnItems : returnSingleItems;
    }

    _toggleOpen = () => {
        this.setState({ open: !this.state.open });
    };

    _togglePreviousOpen = () => {
        this.setState({ previousOpen: !this.state.previousOpen });
    };

    _showDownloads = () => {
        this.setState({ showDownloads: true });
    };

    _closeDownloads = () => {
        this.setState({ showDownloads: false });
    };

    _handleRemoveJourney = (journey) => () => {
        this.setState({ journeyToRemove: journey });
    };

    _handleRemoveJourneyBack = () => {
        this.setState({ journeyToRemove: null });
    };

    _handleRemoveJourneyConfirm = () => {
        OrderActions.deleteTrip(this.props.order, this.props.orderTrip);
        this.setState({ journeyToRemove: null });
    };

    _renderRemoveConfirm() {
        if (this.state.journeyToRemove) {
            return (
                <RemoveJourneyModal
                    journey={this.state.journeyToRemove}
                    onBack={this._handleRemoveJourneyBack}
                    onConfirm={() => {
                        this._handleRemoveJourneyConfirm();
                        this.props.setBasketChanged();
                    }}
                />
            );
        }

        return null;
    }

    renderRemoveJourney(tripTitle) {
        if (!this.props.order.isPaid() && !this.props.isLocked) {
            return (
                <Button className="journey-remove hidden-print btn-plain pull-right" onClick={this._handleRemoveJourney(this.props.orderTrip)}>
                    <span className="sr-only">{t('order.summary.removeJourney')}{tripTitle}</span>
                    <Icon icon="bin" />
                </Button>
            );
        }

        return null;
    }

    getCalculation(ticket) {
        const { storedRefundCalculations } = this.props;

        if (!ticket || !storedRefundCalculations || storedRefundCalculations.size === 0) {
            return null;
        }

        return storedRefundCalculations.tickets.find(c =>
            c.getIn(['calculation', 'ticketId']) === ticket.id
            && c.getIn(['calculation', 'refundAmount']) > 0);
    }

    renderRefundCalculations(orderFare, tripTitle) {
        const { refundDetails, order, context } = this.props;
        const RefundTitleTag = context === OrderSummaryContexts.BASKET_MODAL ? 'h3' : 'h4';

        return (
            <div className="margin-top border--top">
                <RefundTitleTag className="margin-top--sm text-weight--bold">{t('account.orders.refund.tickets')}</RefundTitleTag>
                {orderFare.tickets.map((ticket, index) =>
                    <TicketCalculationsSummary
                        key={index}
                        refundDetails={refundDetails}
                        storedRefundCalculation={this.getCalculation(ticket)}
                        ticket={ticket}
                        tripTitle={tripTitle}
                        isSmartcard={order.isSmartcard()}
                    />
                )}
            </div>
        );
    }

    renderFavourite() {
        const fare = this.getFare();
        const {
            orderTrip,
            order: {
                delivery
            }
        } = this.props;

        if (fare && !fare.isExcludedFromFavourites()) {
            return (
                <AddFavouriteLink
                    fare={fare}
                    orderTrip={orderTrip}
                    delivery={delivery}
                    context="point-checkout"
                />
            );
        }

        return null;
    }

    renderCreateAlert() {
        const { order, orderTrip, additionalOrderTrips } = this.props;

        return (
            <CreateAlert
                order={order}
                orderTrip={orderTrip}
                additionalOrderTrips={additionalOrderTrips}
                context="point-checkout"
            />
        );
    }

    _renderSplitSignpost() {
        const { origin, destination, isReturn, ticketTypeName } =
            deriveSplitDisplayValues(this.getGroupTrips().toArray());

        return (
            <div className="fare-summary split-ticket-summary padding-bottom--sm border--bottom margin-bottom--sm">
                <Row>
                    <Col xs={12} md={6} lg={6}>
                        <FareStations
                            origin={origin}
                            destination={destination}
                            isReturn={isReturn}
                        />
                    </Col>
                    <Col xs={12} md={6} lg={6} className="text-weight--normal ticket-type">
                        {ticketTypeName}
                        <Icon icon="splitticket" className="split-ticket-icon" />
                    </Col>
                </Row>
            </div>
        );
    }

    renderTripActions() {
        const { isLoggedIn, matchingTimetableAlert, context } = this.props;
        const alertProps = matchingTimetableAlert ? matchingTimetableAlert.toJS() : {};
        const { delaysChecked } = alertProps;
        const canCreateAlert = isLoggedIn && this.props.open && !delaysChecked;

        // split journeys support alerts only — favouriting or carbon-calculating
        // a single split part's fare would cover just a segment of the journey
        if (this.isSplitGroup()) {
            if (!canCreateAlert) {
                return null;
            }

            return (
                <Row>
                    <Col xs={12} md={6} className="fare-actions pull-right">
                        {this.renderCreateAlert()}
                    </Col>
                </Row>
            );
        }
        const fare = this.getFare();
        const carbonClassName = `carbon-footprint__${context === OrderSummaryContexts.BASKET_MODAL ? 'basket-modal' : 'payment-confirm-page'}`;

        return (
            <Row>
                <Col xs={12} md={6} className="fare-actions pull-right">
                    {canCreateAlert && this.renderCreateAlert()}
                    {isLoggedIn && this.renderFavourite()}
                </Col>
                <FeatureFlag name='enableCarbonCalculator'>
                    <Col xs={12} md={6} >
                        <CarbonFootprintButton fare={fare} className={carbonClassName} />
                    </Col>
                </FeatureFlag>
            </Row>
        );
    }

    renderPrevious(fareGroups) {
        const { previousOpen } = this.state;
        const { orderTrip } = this.props;
        const dropDownClassNames = classnames('hidden-print text-secondary', {
            'otrl-rotate-180': previousOpen,
        });
        // the plusBus is always included in the amended journey if the original journey had a plusBus
        return (
            <div className="border--top padding-top--sm margin-top--sm">
                <p className="journey-toggler no-margin text-secondary text-weight--normal">
                    <OnEnterTrigger element="span" onClick={this._togglePreviousOpen}>
                        <Icon icon="dropdown" className={dropDownClassNames} />
                        {t('account.orders.amend.previousJourney')}
                    </OnEnterTrigger>

                    {this.renderRemoveJourney()}
                </p>
                {fareGroups.map((group, key) => (group.length > 1
                    // a passenger's split legs — one consolidated split-item row, matching the new/current side
                    ? <AmendedSplitFares key={key} fares={group} />
                    // a single (non-split) old fare — keep the existing full fare card
                    : (
                        <Fare
                            key={group[0].fareId}
                            orderFare={group[0]}
                            open={previousOpen}
                            isFareOnly={this.props.isFareOnly}
                            isAmended
                        />
                    )))}

                <TripPlusbus key="1" orderTrip={orderTrip} open={previousOpen} />
            </div>
        );
    }

    render() {
        const {
            order,
            orderTrip,
            index,
            isFareOnly,
            showAmendedTrip,
            context,
            matchingTimetableAlert,
            isLoggedIn,
            expandFaresPerTicket,
        } = this.props;

        const { open } = this.state;
        const orderFare = this.getFare();
        const groupTrips = this.getGroupTrips();
        const fares = groupTrips.flatMap(trip => trip.getJourneyFares());
        const displayFares = expandFaresPerTicket
            ? fares.flatMap(fare => fare.tickets.map(ticket => fare.set('tickets', new List([ticket]))))
            : fares;

        const isSplit = this.isSplitGroup();
        // per-passenger split rows (the amend Summary step) render one consolidated
        // AmendedSplitFares item per passenger instead of a signpost + per-leg Fare rows,
        // so the alert-index bookkeeping below (only consumed by <Fare>) doesn't apply.
        const isSplitPerPassengerView = isSplit && expandFaresPerTicket;
        let lastOutwardAlertIndex = -1;
        let lastReturnAlertIndex = -1;

        if (isSplit && !isSplitPerPassengerView) {
            const returnTripsSet = new Set(this.getSplitReturnTrips());
            let runningIndex = -1;

            groupTrips.forEach(trip => {
                const tripFareCount = trip.getJourneyFares().size;
                runningIndex += tripFareCount;
                if (returnTripsSet.has(trip)) {
                    lastReturnAlertIndex = runningIndex;
                } else {
                    lastOutwardAlertIndex = runningIndex;
                }
            });
        }
        const titleKey = orderTrip.isSmartcard() ? 'smartcard_title' : 'journey_title';
        const tripTitle = t(`order.summary.${titleKey}`, { number: index + 1 });
        const TripTitleTag = context === OrderSummaryContexts.BASKET_MODAL ? 'h2' : 'h3';

        const classNames = classnames('order-summary__item', {
            'order-summary__item--expanded': open,
        });
        const dropDownClassNames = classnames('hidden-print text-secondary', {
            'otrl-rotate-180': open,
        });
        const amendedTicketGroups = order.getGroupedOldFaresForFares(fares);

        return (
            <li className={classNames}>
                {this._renderRemoveConfirm()}

                <div className="box-headed box-headed--primary">
                    <div className="box-headed__header box--padded text-weight--normal">
                        <Row>
                            <Col xs={8}>
                                <TripTitleTag className="journey-heading text-weight--normal fake-header">
                                    {tripTitle}
                                </TripTitleTag>
                            </Col>

                            <Col xs={4} className="text-right price">
                                <Price price={this.getGroupTotalPrice()} />
                            </Col>
                        </Row>
                    </div>

                    <FeatureFlag name="enableTicketDownload">
                        {order.hasDownloadableTickets() && (
                            <div className="box box--padded box-headed__body">
                                <Row>
                                    <Col xsOffset={8} xs={4} className="text-right price">
                                        <Button onClick={this._showDownloads}>{t('order.summary.download.tickets')}</Button>
                                        {this.state.showDownloads && <DownloadTicketsModal onClose={this._closeDownloads} {...{ order, orderTrip }} />}
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </FeatureFlag>

                    <div className="box-headed__body box--padded">
                        <p className="journey-toggler no-margin text-secondary text-weight--normal">
                            <OnEnterTrigger element="span" role="button" onClick={this._toggleOpen}>
                                <Icon icon="dropdown" className={dropDownClassNames} />
                                {t(showAmendedTrip && amendedTicketGroups.length ? 'account.orders.amend.journey' : 'order.summary.journey_details')}
                                <span className="sr-only">. {open ? t('global.clickToCollapse') : t('global.clickToExpand')}</span>
                            </OnEnterTrigger>

                            {this.renderRemoveJourney(tripTitle)}
                        </p>

                        {isSplit && !isSplitPerPassengerView && this._renderSplitSignpost()}

                        {isSplitPerPassengerView
                            ? groupFaresByPassenger(fares).map((passengerFares, key) => (
                                <AmendedSplitFares key={key} fares={passengerFares} />
                            ))
                            : displayFares.map((fare, key) => (
                                <Fare
                                    orderFare={fare}
                                    key={key}
                                    open={open}
                                    isFareOnly={isFareOnly}
                                    isSmartcard={order.isSmartcard()}
                                    matchingTimetableAlert={matchingTimetableAlert}
                                    isLoggedIn={isLoggedIn}
                                    orderTrip={orderTrip}
                                    idx={key}
                                    showAlertMessage={!isSplit || key === lastOutwardAlertIndex || key === lastReturnAlertIndex}
                                />
                            ))}

                        <TripPlusbus key="0" orderTrip={this.getTripWithPlusBus()} open={open} />

                        {this.renderTripActions()}

                        {showAmendedTrip && Boolean(amendedTicketGroups.length) && this.renderPrevious(amendedTicketGroups)}

                        {order.hasRefunds() && orderFare.tickets.some(ticket => ticket.isRefunded) && this.renderRefundCalculations(orderFare, tripTitle)}
                    </div>
                </div>
            </li>
        );
    }
}

export default Trip;
